import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Info, Phone } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import img1 from '../Images/heatprocessing.jpg';
import img2 from "../gW/b1.png";
import img3 from "../gW/b2.png";
import img4 from "../gW/b3.png";
import img5 from "../gW/b4.png";
import img6 from "../gW/b5.png";
import img7 from "../gW/b6.png";
import img8 from "../gW/b7.png";

const ProcessHeatingPage = () => {
  const [activeTab, setActiveTab] = useState('steam');
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const categories = {
    steam: {
      title: "Steam Boilers",
      products: [
        {
          name: "Oil Fired Boiler (Coil Type)",
          description: "A versatile, fully automatic, instant steam generating range of boilers designed for safe and reliable operations. Easy to install and commission, these time-tested boilers require very little maintenance.",
          features: [
            "Efficiency of 88% on NCV",
            "Reverse flame technology",
            "Unique patented membrane design",
            "Built-in heat recovery device",
            "Membrane design allows large tube diameter",
            "Unique economizer – optimizer design",
            "Unique circulation burner design",
            "Ceramic wool refractory",
            "Easy access to all parts",
            "Powder-coated, well lit control panel"
          ],
          range: {
            capacity: "100 to 850 Kg/hr",
            fuels: "Light oil, gas or dual fuel"
          },
          image: img2
        },
        {
          name: "Oil Fired Boiler (Shell Type)",
          description: "A high-efficiency shell type boiler designed for versatile industrial applications with advanced monitoring and control capabilities.",
          features: [
            "100% Skid mounted Plug and Play boiler (up to 10 TPH)",
            "Multi-fuel option – light oils/heavy oils/gases",
            "High dryness fraction steam increases usable heat",
            "Comes with mono-block/dual block/steam atomized burner with world renowned technology",
            "Large water holding capacity",
            "Optional 3-element control systems for close monitoring of water level, steam flow & feed water flow",
            "HMI and PLC panels for advance boiler monitoring & control",
            "Overall efficiency of up-to 95% with sensible heat recovery units"
          ],
          range: {
            capacity: "Standard 1 to 16 TPH, customized designs available upto 34 TPH F&A 100 deg cel",
            fuels: "Standard – LDO, FO, LSHS & natural gas, customized design for other oil & gas fuels"
          },
          image: img3
        },
        {
          name: "Solid Fuel Fired Boiler",
          description: "Completely paneled and ultra-compact solid fuel boilers with integrated heat recovery for high fuel efficiency.",
          features: [
            "Completely Paneled and Ultra-compact",
            "Integrated Heat Recovery Unit – High Fuel Efficiency",
            "Bigger Fire Door – Easy Fuel Feeding",
            "Centrifugal Pump – Highly Flexible",
            "FD & ID Fan – No Back Firing",
            "Automatic Pressure Switch",
            "Bubbling bed combustion",
            "Top Loading design",
            "Special ferrules protection",
            "CFD designed nozzles"
          ],
          range: {
            capacity: "500 & 750 kg/hr (7 kg/cm2), 1.0 & 1.5 TPH (10.54 kg/cm2), 2 to 5 TPH, 5-30 TPH",
            fuels: "Coal, Wood Logs, Briquettes, Rice husk, Wood Chips, Paddy Husk, Biomass Pellets"
          },
          image: img4
        }
      ]
    },
    thermic: {
      title: "Thermic Fluid Heater",
      products: [
        {
          name: "Oil Fired TF Heater",
          description: "Designed for maximum efficiency, using a variety of liquid and gaseous fuels with fuel flexibility in mind.",
          features: [
            "High capacity heating system",
            "Multi-fuel option – heavy oils, light oils and gases",
            "Requires very little maintenance",
            "Large in-bed and convective heat transfer area",
            "High efficiency of 85% (+/-2%) on NCV"
          ],
          range: {
            capacity: "0.1 million kcal/hr to 12.0 million kcal/hr",
            temperature: "up to 320ºC",
            fuels: "heavy oils, light oils and gases"
          },
          image: img5
        },
        {
          name: "Solid Fuel Fired TF Heater",
          description: "Designed for maximum efficiency using various solid fuels including agro-waste fuels. Allows high process temperatures at low operating pressures.",
          features: [
            "Suitable for coal, wood, sawdust baggase, rice husk",
            "Option of traveling grate/flat grate/bubbling bed",
            "On-site furnace construction",
            "Comprehensive combustion system",
            "Complete pollution control equipment"
          ],
          range: {
            capacity: "0.1 to 17.0 million kcal/hr",
            fuels: "Solid fuels/agro-fuels"
          },
          image: img6
        }
      ]
    },
    air: {
      title: "Hot Air Heater",
      products: [
        {
          name: "Oil Fired Hot Air Heater",
          description: "Produces hot Air at temperatures up to 140ºC within minutes of cold start.",
          features: [
            "Three-pass thermally efficient design",
            "Fully automatic operation",
            "Multi-fuel option – light/heavy oils/gases",
            "High efficiency of about 80%",
            "Custom-built temperature up to 300ºC"
          ],
          specifications: {
            fuelType: "Gasoline, Natural Gas",
            temperature: "Up to 300 Deg C",
            material: "Mild Steel",
            applications: "Agriculture, Food Industry, Beverage Industry",
            finishing: "Powder Coated",
            display: "Digital"
          },
          image: img7
        },
        {
          name: "Solid Fuel Fired Hot Air Heater",
          description: "Multi solid fuel fired air heater offering high thermal efficiency, steady heat and constant air temperature.",
          features: [
            "Three-Pass indirect smoke system",
            "High temperature resistant stainless steel",
            "Cast iron burner grate",
            "Under stoker feeding system",
            "Speed adjustable Gear motor",
            "Optimized combustion air system",
            "Multi-Cyclone dust separator option"
          ],
          optionals: [
            "Electro-mechanical board",
            "Electronic board for automatic ignition",
            "Ashes extractor device",
            "Retards for heat maintenance",
            "Automatic fuel feeding",
            "Fire-fighting system",
            "Rotary valve"
          ],
          range: {
            capacity: "80 Kw/hr to 200 kw/hr"
          },
          image: img8
        }
      ]
    }
  };

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Process Heating Solutions",
    "description": "Comprehensive industrial heating solutions including steam boilers, thermic fluid heaters, and hot air heaters",
    "itemListElement": Object.entries(categories).flatMap(([catKey, category], catIndex) => 
      category.products.map((product, prodIndex) => ({
        "@type": "Product",
        "position": catIndex * 10 + prodIndex + 1,
        "name": product.name,
        "description": product.description,
        "image": product.image,
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "seller": {
            "@type": "Organization",
            "name": "Gortherm Energy"
          }
        }
      }))
    )
  };

  return (
    <>
      <Helmet>
        <title>Process Heating Solutions | Steam Boilers, Thermic Fluid & Hot Air Heaters | Gortherm Energy</title>
        <meta name="description" content="Comprehensive industrial process heating solutions including oil-fired and solid fuel steam boilers, thermic fluid heaters, and hot air heaters. High-efficiency heating systems for industrial applications." />
        <meta name="keywords" content="steam boilers, thermic fluid heater, hot air heater, oil fired boiler, solid fuel boiler, process heating, industrial boilers, coil type boiler, shell type boiler, thermal fluid heater, biomass boiler, industrial heating Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Process Heating Solutions | Steam Boilers & Heaters | Gortherm Energy" />
        <meta property="og:description" content="Advanced industrial heating solutions designed for efficiency and reliability. Steam boilers, thermic fluid heaters, and hot air heating systems." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/process-heating" />
        <meta property="og:image" content={img1} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Process Heating Solutions | Gortherm Energy" />
        <meta name="twitter:description" content="High-efficiency industrial heating systems for steam generation and process heating applications." />
        <meta name="twitter:image" content={img1} />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/process-heating" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Hero Section */}
        <motion.header
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative h-[300px] sm:h-[400px] lg:h-[500px] overflow-hidden"
        >
          <img
            src={img1}
            alt="Industrial process heating solutions - steam boilers and heaters by Gortherm Energy"
            className="w-full h-full object-cover transform scale-105"
            loading="eager"
            width="1920"
            height="500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="max-w-2xl"
              >
                <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tight mb-4 sm:mb-6 leading-tight">
                  Process Heating
                  <span className="text-red-500"> Solutions</span>
                </h1>
                <p className="text-base sm:text-lg lg:text-xl text-gray-200 mb-6 sm:mb-8">
                  Discover our comprehensive range of industrial heating solutions designed for efficiency and reliability
                </p>
              </motion.div>
            </div>
          </div>
        </motion.header>

        {/* Category Tabs */}
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" aria-label="Heating solution categories">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex justify-center items-center flex-wrap gap-4 sm:gap-6"
          >
            {Object.entries(categories).map(([key, category]) => (
              <motion.button
                key={key}
                variants={itemVariants}
                onClick={() => setActiveTab(key)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`flex items-center space-x-3 sm:space-x-6 p-4 sm:p-6 rounded-xl shadow-lg transition-all duration-300
                  ${activeTab === key
                    ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-red-200'
                    : 'bg-white text-gray-700 hover:shadow-xl'}`}
                aria-pressed={activeTab === key}
              >
                <span className="text-base sm:text-lg font-semibold">{category.title}</span>
              </motion.button>
            ))}
          </motion.div>
        </nav>

        {/* Products Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <AnimatePresence mode="wait">
            {Object.entries(categories).map(([key, category]) => (
              activeTab === key && (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="space-y-8 sm:space-y-12"
                >
                  {category.products.map((product, idx) => (
                    <motion.article
                      key={product.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      onHoverStart={() => setHoveredCard(idx)}
                      onHoverEnd={() => setHoveredCard(null)}
                      className={`bg-white rounded-2xl sm:rounded-3xl shadow-xl transition-all duration-300 
                        ${hoveredCard === idx ? 'shadow-2xl transform scale-[1.01] sm:scale-[1.02]' : ''}`}
                      itemScope
                      itemType="https://schema.org/Product"
                    >
                      <div className="p-4 sm:p-6 lg:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
                          <div className="space-y-4 sm:space-y-6">
                            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 flex items-center space-x-2" itemProp="name">
                              <span>{product.name}</span>
                              <Info className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 flex-shrink-0" aria-hidden="true" />
                            </h3>
                            <p className="text-sm sm:text-base lg:text-lg text-gray-600 leading-relaxed" itemProp="description">
                              {product.description}
                            </p>
                            <motion.div
                              className="relative h-48 sm:h-64 lg:h-72 rounded-xl sm:rounded-2xl overflow-hidden shadow-lg"
                              whileHover={{ scale: 1.03 }}
                              transition={{ duration: 0.3 }}
                            >
                              <img
                                src={product.image}
                                alt={`${product.name} - Industrial heating equipment`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                width="600"
                                height="400"
                                itemProp="image"
                              />
                            </motion.div>
                          </div>

                          <div className="space-y-6 sm:space-y-8">
                            <div className="bg-red-50 p-4 sm:p-6 lg:p-8 rounded-xl sm:rounded-2xl">
                              <h4 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-4 sm:mb-6 text-red-500">
                                Key Features
                              </h4>
                              <ul className="grid gap-2 sm:gap-4">
                                {product.features.map((feature, i) => (
                                  <li key={i} className="flex items-start space-x-2 sm:space-x-3">
                                    <span className="text-red-500 mt-1 text-sm sm:text-base" aria-hidden="true">•</span>
                                    <span className="text-xs sm:text-sm lg:text-base text-gray-700">{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <button
                              onClick={() => setExpandedProduct(expandedProduct === idx ? null : idx)}
                              className="w-full bg-gray-50 p-4 sm:p-6 rounded-xl flex items-center justify-between text-gray-700 hover:bg-gray-100 transition-all duration-300"
                              aria-expanded={expandedProduct === idx}
                              aria-controls={`specs-${idx}`}
                            >
                              <span className="font-medium text-sm sm:text-base lg:text-lg">Technical Specifications</span>
                              <motion.div
                                animate={{ rotate: expandedProduct === idx ? 180 : 0 }}
                                transition={{ duration: 0.3 }}
                              >
                                <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6" />
                              </motion.div>
                            </button>

                            <AnimatePresence>
                              {expandedProduct === idx && (
                                <motion.div
                                  id={`specs-${idx}`}
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="space-y-4 sm:space-y-6 bg-gray-50 p-4 sm:p-6 lg:p-8 rounded-xl"
                                >
                                  {product.range && (
                                    <div>
                                      <h4 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 mb-2">Range</h4>
                                      <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm lg:text-base text-gray-600">
                                        {Object.entries(product.range).map(([key, value]) => (
                                          <li key={key}>
                                            <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {value}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {product.specifications && (
                                    <div>
                                      <h4 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 mb-2">Specifications</h4>
                                      <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm lg:text-base text-gray-600">
                                        {Object.entries(product.specifications).map(([key, value]) => (
                                          <li key={key}>
                                            <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {value}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {product.optionals && (
                                    <div>
                                      <h4 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-800 mb-2">Optional Features</h4>
                                      <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm lg:text-base text-gray-600">
                                        {product.optionals.map((optional, i) => (
                                          <li key={i}>{optional}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </motion.div>
              )
            ))}
          </AnimatePresence>
        </section>

        {/* Why Choose Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="bg-white rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">
              Why Choose Our Process Heating Solutions?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 text-gray-700">
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-red-600">
                  High Efficiency
                </h3>
                <p className="text-sm sm:text-base">
                  Our heating systems deliver exceptional thermal efficiency up to 95%, reducing fuel 
                  consumption and operational costs while maximizing heat transfer.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-red-600">
                  Multi-Fuel Flexibility
                </h3>
                <p className="text-sm sm:text-base">
                  Compatible with various fuel types including oil, gas, coal, biomass, and agro-waste, 
                  providing operational flexibility and cost optimization.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-red-600">
                  Advanced Controls
                </h3>
                <p className="text-sm sm:text-base">
                  Fully automated systems with PLC controls, HMI panels, and real-time monitoring 
                  ensure precise temperature control and safe operation.
                </p>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 text-red-600">
                  Low Maintenance
                </h3>
                <p className="text-sm sm:text-base">
                  Robust construction, quality components, and accessible design minimize maintenance 
                  requirements and maximize uptime for continuous production.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <aside className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 sm:p-8 lg:p-12 shadow-lg text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Need the Right Heating Solution?
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Our process heating experts are ready to help you select and implement 
              the perfect heating system for your industrial requirements.
            </p>
            <a 
              href="tel:+9779802781400"
              className="inline-block"
              aria-label="Contact process heating experts at +977 980 278 1400"
            >
              <button className="bg-red-600 hover:bg-red-700 active:bg-red-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 rounded-lg flex items-center gap-2 sm:gap-3 mx-auto transition-all duration-300 shadow-md hover:shadow-xl text-sm sm:text-base lg:text-lg font-semibold">
                <Phone className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                <span>Contact Our Experts</span>
              </button>
            </a>
            
          </div>
        </aside>
      </main>
    </>
  );
};

export default ProcessHeatingPage;